import { TooltipPlacement } from './Tooltip.enums';
/**
 *  REMOVE when Popover-API and anchor-positioning is available in latest Firefox ESR
 */
/** Spacing between trigger and tooltip (rem) */
const POPOVER_GAP = 0.3125; // 5px / 16

/** Convert px to rem using current root font size */
function toRem(px: number): string {
  const rootFontSize = window.getComputedStyle(document.documentElement).fontSize;
  const fontSize = parseFloat(rootFontSize);
  return `${(px / fontSize).toFixed(4)}rem`;
}

/**
 * Determines whether the tooltip was flipped to the opposite side of the trigger
 * (via position-try-fallbacks or the JS fallback), so the arrow can be kept
 * pointing at the trigger.
 */
export function computeFlippedState(
  placement: TooltipPlacement,
  anchorRect: Pick<DOMRect, 'top' | 'bottom' | 'left' | 'right'>,
  popoverRect: Pick<DOMRect, 'top' | 'bottom' | 'left' | 'right'>
): boolean {
  switch (placement) {
    case TooltipPlacement.TOP:
    case TooltipPlacement.AUTO:
      return popoverRect.top > anchorRect.bottom;
    case TooltipPlacement.BOTTOM:
      return popoverRect.bottom < anchorRect.top;
    case TooltipPlacement.LEFT:
      return popoverRect.left > anchorRect.right;
    case TooltipPlacement.RIGHT:
      return popoverRect.right < anchorRect.left;
  }
}

/** Position a popover element relative to its trigger using manual fixed positioning.
 *  Fallback for browsers without anchor-positioning. All values in rem.
 * */

export function positionPopoverFallback(
  attachToElement: HTMLElement,
  popover: HTMLElement,
  placement: TooltipPlacement
) {
  const rootFontSize = parseFloat(window.getComputedStyle(document.documentElement).fontSize);
  const triggerRect = attachToElement.getBoundingClientRect();
  const popoverRect = popover.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const gapPx = POPOVER_GAP * rootFontSize;
  const edgePx = 4;

  popover.style.inset = 'auto';
  popover.style.position = 'fixed';
  popover.style.margin = '0';

  switch (placement) {
    case TooltipPlacement.TOP:
    case TooltipPlacement.AUTO: {
      let left = triggerRect.left + triggerRect.width / 2 - popoverRect.width / 2;
      let top = triggerRect.top - popoverRect.height - gapPx;
      if (top < edgePx) {
        // not enough space above the trigger -> flip below
        top = triggerRect.bottom + gapPx;
      }
      if (left < edgePx) {
        left = edgePx;
      }
      if (left + popoverRect.width > vw - edgePx) {
        left = vw - popoverRect.width - edgePx;
      }
      if (top + popoverRect.height > vh - edgePx) {
        top = vh - popoverRect.height - edgePx;
      }
      if (top < edgePx) {
        top = edgePx;
      }
      popover.style.top = toRem(top);
      popover.style.left = toRem(left);
      break;
    }
    case TooltipPlacement.BOTTOM: {
      let left = triggerRect.left + triggerRect.width / 2 - popoverRect.width / 2;
      let top = triggerRect.bottom + gapPx;
      if (top + popoverRect.height > vh - edgePx) {
        // not enough space below the trigger -> flip above
        top = triggerRect.top - popoverRect.height - gapPx;
      }
      if (left < edgePx) {
        left = edgePx;
      }
      if (left + popoverRect.width > vw - edgePx) {
        left = vw - popoverRect.width - edgePx;
      }
      if (top < edgePx) {
        top = edgePx;
      }
      popover.style.top = toRem(top);
      popover.style.left = toRem(left);
      break;
    }
    case TooltipPlacement.RIGHT: {
      let top = triggerRect.top + triggerRect.height / 2 - popoverRect.height / 2;
      let left = triggerRect.right + gapPx;
      if (left + popoverRect.width > vw - edgePx) {
        // not enough space on the right -> flip to the left
        left = triggerRect.left - popoverRect.width - gapPx;
      }
      if (top < edgePx) {
        top = edgePx;
      }
      if (top + popoverRect.height > vh - edgePx) {
        top = vh - popoverRect.height - edgePx;
      }
      popover.style.top = toRem(top);
      popover.style.left = toRem(left);
      break;
    }
    case TooltipPlacement.LEFT: {
      let top = triggerRect.top + triggerRect.height / 2 - popoverRect.height / 2;
      let left = triggerRect.left - popoverRect.width - gapPx;
      if (left < edgePx) {
        // not enough space on the left -> flip to the right
        left = triggerRect.right + gapPx;
      }
      if (top < edgePx) {
        top = edgePx;
      }
      if (top + popoverRect.height > vh - edgePx) {
        top = vh - popoverRect.height - edgePx;
      }
      popover.style.top = toRem(top);
      popover.style.left = toRem(left);
      break;
    }
  }
}
